import { inject } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';

import { map, take } from 'rxjs';

import { ProfileService } from '@core/services/profile.service';

import { type UserRole } from '@models/user.model';

/**
 * Factory guard — redirects users who don't have the required role to /access-denied.
 * When the server cannot be reached the verdict is unknown: that lands on
 * /access-denied?reason=unreachable (with a retry back to the wanted page), not on "forbidden".
 *
 * Usage in routes:
 *   canActivate: [authGuard, chefGuard]
 */
export const roleGuard = (requiredRole: UserRole): CanActivateFn => {
  return (_route, state) => {
    const profile = inject(ProfileService);
    const router = inject(Router);

    return profile.roleAccess(requiredRole).pipe(
      take(1),
      map((access) => {
        if (access === 'allowed') {return true;}
        return access === 'unreachable'
          ? router.createUrlTree(['/access-denied'], { queryParams: { reason: 'unreachable', returnUrl: state.url } })
          : router.createUrlTree(['/access-denied'], { queryParams: { reason: 'forbidden' } });
      }),
    );
  };
};

/** Convenience alias for the chef-or-admin role check (Portal-SPA is Chef/Admin-only). */
export const chefGuard: CanActivateFn = roleGuard('chef');
